<template>
  <v-container
    class="sale-layout"
    fluid
    :class="[
      !isLoading && !localProducts.length ? 'no-data' : '',
      { 'sale-layout--dark': $vuetify.theme.dark },
    ]"
    :aria-busy="isLoading ? 'true' : 'false'"
  >
    <!-- Price Channel Bar -->
    <div
      v-if="priceChannels && priceChannels.length > 0"
      class="ps-sale-channel-bar mb-2 d-flex align-center flex-wrap"
    >
      <div class="d-flex align-center mr-2">
        <v-icon x-small color="primary" class="mr-1">fal fa-tag</v-icon>
        <span class="text-caption font-weight-bold grey--text text--darken-1">
          {{ $t("price_channel") }}:
        </span>
      </div>
      <v-chip-group
        :value="activeChannelChipValue"
        mandatory
        active-class="primary white--text font-weight-bold elevation-1"
        class="ps-sale-channel-chips"
        @change="handleChannelChipChange"
      >
        <v-chip
          small
          outlined
          filter
          class="channel-chip font-weight-medium"
        >
          {{ $t("default_price") }}
        </v-chip>
        <v-chip
          v-for="ch in priceChannels"
          :key="ch.id"
          small
          outlined
          filter
          class="channel-chip font-weight-medium"
        >
          <v-icon v-if="ch.icon" left x-small>{{ ch.icon }}</v-icon>
          {{ ch.name }}
        </v-chip>
      </v-chip-group>
    </div>

    <div class="sale-product-list">
      <template v-if="isLoading && !localProducts.length">
        <div class="row sale-skeleton-grid" aria-hidden="true">
          <div
            v-for="index in 20"
            :key="`skeleton-${index}`"
            :class="cardsPerRowClass"
          >
            <div class="main-card">
              <v-card rounded class="rounded-lg">
                <v-skeleton-loader type="card, list-item" />
              </v-card>
            </div>
          </div>
        </div>
      </template>
      <template v-else-if="localProducts.length">
        <div class="row">
          <div
            v-for="(item, index) in localProducts"
            :key="item.id || index"
            :class="cardsPerRowClass"
          >
            <app-card
              :item="item"
              @show-variation-modal="handleShowVariationModal"
              @show-dynamic-price-modal="handleShowDynamicPriceModal"
              @show-pack-modal="handleShowPackModal"
            />
          </div>
        </div>
      </template>
      <app-no-product v-else />
      <!-- Infinite scroll loading spinner -->
      <div class="text-center my-4" v-if="isLoading && localProducts.length">
        <v-progress-circular indeterminate color="primary" size="24" />
      </div>
      <!-- Sentinel for IntersectionObserver -->
      <div ref="infiniteSentinel" style="height: 1px"></div>
    </div>

    <!-- Product Selection Modal -->
    <ProductSelectionModal
      v-if="selectedProduct"
      :key="selectedProduct.id + '-' + showVariationModal"
      :show.sync="showVariationModal"
      :product="selectedProduct"
      @update:show="handleCloseVariationModal"
    />

    <!-- Dynamic Price Modal -->
    <DynamicPriceModal
      v-if="selectedProductForDynamicPrice"
      :key="selectedProductForDynamicPrice.id + '-' + showDynamicPriceModal"
      :show.sync="showDynamicPriceModal"
      :product="selectedProductForDynamicPrice"
      @update:show="handleCloseDynamicPriceModal"
    />

    <!-- Pack Quantity Modal -->
    <PackQuantityModal
      v-if="selectedProductForPack"
      :key="
        (selectedProductForPack && selectedProductForPack.id) +
        '-' +
        showPackModal
      "
      :show.sync="showPackModal"
      :product="selectedProductForPack"
      @update:show="handleClosePackModal"
    />
  </v-container>
</template>

<script>
import AppCard from "@/components/products/card.vue";
import AppNoProduct from "@/components/products/no-product";
import ProductSelectionModal from "@/components/products/ProductSelectionModal";
import DynamicPriceModal from "@/components/products/DynamicPriceModal";
import PackQuantityModal from "@/components/products/PackQuantityModal.vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { mainStore } from "@/stores/modules/cart";
import { toast, confirm, restoreAppFocus } from "@/controllers/alert";

export default {
  data() {
    return {
      defineStore: mainStore(),
      showVariationModal: false,
      selectedProduct: null,
      showDynamicPriceModal: false,
      selectedProductForDynamicPrice: null,
      showPackModal: false,
      selectedProductForPack: null,
      // infinite scroll state
      page: 1,
      perPage: 30,
      isLoading: false,
      hasMore: true,
      localProducts: [],
      observer: null,
      // Table information from tables page
      selectedTable: null,
      // Category tracking (local copy for comparison)
      localCategoryId: 0,
      // Mobile detection
      windowWidth: 0,
      // Cards per row configuration
      cardsPerRow: 4,
    };
  },
  mounted() {
    this.setupLiveListeners();
    this.fetchPriceChannels();

    // Initialize local category ID from Vuex
    this.$nextTick(() => {
      this.localCategoryId = this.selectedCategoryId || 0;
      restoreAppFocus();
      this.$root.$emit("focus-scanner");
    });
  },

  beforeDestroy() {
    this.teardownLiveListeners();
  },
  // The sale page is kept alive (see utils/keepAlive.js): drop window/document listeners and the
  // infinite-scroll observer while hidden, and re-attach + re-sync the table when shown again.
  deactivated() {
    this._saleDeactivated = true;
    this.teardownLiveListeners();
  },
  activated() {
    if (!this._saleDeactivated) return; // first activation: created()/mounted() did the setup
    this._saleDeactivated = false;
    this.setupLiveListeners();
    this.fetchPriceChannels();
    this.syncTableFromRoute();
    this.$nextTick(() => {
      restoreAppFocus();
      this.$root.$emit("focus-scanner");
    });
  },
  components: {
    AppCard,
    AppNoProduct,
    ProductSelectionModal,
    DynamicPriceModal,
    PackQuantityModal,
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      store_type: "auth/store_type",
      selectedCategoryId: "index/selectedCategoryId",
    }),

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    priceChannels() {
      return this.defineStore.priceChannels || [];
    },

    activeChannelChipValue() {
      if (!this.defineStore.selectedPriceChannelId) return 0;
      const idx = this.priceChannels.findIndex(
        (c) => Number(c.id) === Number(this.defineStore.selectedPriceChannelId)
      );
      return idx >= 0 ? idx + 1 : 0;
    },

    cardsPerRowClass() {
      if (this.$vuetify.breakpoint.xs) {
        return "w-50"; // Always 2 cards per row on mobile
      }
      const classMap = {
        3: "w-33",
        4: "w-25",
        5: "w-20",
      };
      return classMap[this.cardsPerRow] || "w-25";
    },
  },

  watch: {
    // Watch Vuex selectedCategoryId for real-time updates
    selectedCategoryId: {
      handler(newCategoryId) {
        if (newCategoryId !== undefined && newCategoryId !== null) {
          this.handleCategoryChange(newCategoryId);
        }
      },
      immediate: false,
    },
    // Reload products when the table changes so per-table buffet flag is fresh.
    "$route.query.tableId": {
      handler(newId, oldId) {
        // kept alive: ignore route changes that happen while this page is hidden
        if (this.$route.path !== "/sale") return;
        if (newId !== oldId) {
          this.loadFirstPage();
        }
      },
      immediate: false,
    },
  },
  created() {
    // Initialize local category ID from Vuex
    this.localCategoryId = this.selectedCategoryId || 0;
    // Load cards per row configuration
    this.loadCardsPerRow();
    this.loadFirstPage();
    this.syncTableFromRoute();
  },
  methods: {
    setupLiveListeners() {
    // Check initial screen size
    this.checkScreenSize();
    window.addEventListener("resize", this.handleResize);

    // Load cards per row from localStorage
    this.loadCardsPerRow();

    // Listen for storage changes to update cards per row when changed in settings
    window.addEventListener("storage", this.handleStorageChange);

    // Listen for visibility changes to reload config when user returns to tab
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    // Setup IntersectionObserver for infinite scrolling
    const options = {
      root: null,
      rootMargin: "200px",
      threshold: 0,
    };
    this.observer = new IntersectionObserver(async (entries) => {
      const entry = entries[0];
      if (entry && entry.isIntersecting && this.hasMore && !this.isLoading) {
        await this.loadNextPage();
      }
    }, options);
    if (this.$refs.infiniteSentinel) {
      this.observer.observe(this.$refs.infiniteSentinel);
    }
    },
    teardownLiveListeners() {
      window.removeEventListener("resize", this.handleResize);
      window.removeEventListener("storage", this.handleStorageChange);
      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange,
      );
      if (this.observer) {
        if (this.$refs.infiniteSentinel) {
          this.observer.unobserve(this.$refs.infiniteSentinel);
        }
        this.observer.disconnect();
        this.observer = null;
      }
    },
    // Pre-selected table comes from the route (?tableId=&tableName=); keep it in sync on re-entry
    syncTableFromRoute() {
      const tableId = this.$route.query.tableId || this.$route.query.table_id;
      const tableName =
        this.$route.query.tableName || this.$route.query.table_name;
      this.selectedTable = tableId
        ? { id: tableId, name: tableName || `Table ${tableId}` }
        : null;
    },
    // called by utils/keepAlive.js when data may be stale after a write / long absence
    onReactivate() {
      this.loadFirstPage();
    },
    ...mapMutations({
      SET_PAYMENT: "index/SET_PAYMENT",
    }),

    ...mapActions({
      fetch: "index/fetch",
    }),

    async fetchPriceChannels() {
      try {
        const { data } = await this.$http.get("price_channels?is_active=1");
        if (data && data.status && Array.isArray(data.data)) {
          this.defineStore.setPriceChannels(data.data);
        }
      } catch (e) {
        console.error("fetchPriceChannels error:", e);
      }
    },

    handleChannelChipChange(chipIndex) {
      if (chipIndex === 0 || chipIndex == null) {
        this.defineStore.setPriceChannel(null);
      } else {
        const ch = this.priceChannels[chipIndex - 1];
        if (ch) {
          this.defineStore.setPriceChannel(ch.id);
        }
      }
    },

    // Mobile detection methods
    checkScreenSize() {
      this.windowWidth = window.innerWidth;
    },

    handleResize() {
      this.checkScreenSize();
    },

    async loadFirstPage() {
      this.page = 1;
      this.localProducts = [];
      this.hasMore = true;
      // Use current category ID from Vuex when loading first page
      await this.loadPage(this.page, this.selectedCategoryId);
    },
    async loadNextPage() {
      if (!this.hasMore || this.isLoading) return;
      this.page += 1;
      await this.loadPage(this.page);
    },
    handleCategoryChange(newCategoryId) {
      // Only reload if category actually changed
      if (this.localCategoryId !== newCategoryId) {
        this.localCategoryId = newCategoryId;
        // Reset pagination and reload products with new category
        this.loadFirstPage();
      }
    },
    async loadPage(page, catId = null) {
      try {
        this.isLoading = true;
        // Use direct HTTP to avoid overwriting Vuex store list
        // Build URL with optional cat_id parameter
        let url = `products_paginate?page=${page}&per_page=${this.perPage}`;
        const categoryId = catId !== null ? catId : this.selectedCategoryId;
        if (categoryId && categoryId !== 0) {
          url += `&cat_id=${categoryId}`;
        }
        // Per-table buffet flag: backend uses table_id to mark covered categories as is_buffet_included.
        const tableId = this.$route.query.tableId || this.$route.query.table_id;
        if (tableId) {
          url += `&table_id=${tableId}`;
        }
        const { data } = await this.$http.get(url);
        if (data && data.status && data.data && data.data.data) {
          const rows = data.data.data;
          // Append while avoiding duplicates by id
          const existingIds = new Set(this.localProducts.map((p) => p.id));
          const toAppend = rows.filter((p) => !existingIds.has(p.id));
          this.localProducts = this.localProducts.concat(toAppend);
          // Determine if more pages available
          const meta = data.data;
          if (
            meta.current_page >= meta.last_page ||
            rows.length < this.perPage
          ) {
            this.hasMore = false;
          }
        } else if (Array.isArray(data)) {
          // Fallback if API returns array
          const rows = data || [];
          this.localProducts = this.localProducts.concat(rows);
          if (rows.length < this.perPage) this.hasMore = false;
        } else {
          this.hasMore = false;
        }
      } catch (e) {
        console.error("Failed to load products page", e);
      } finally {
        this.isLoading = false;
      }
    },
    async removeCart() {
      // check if cart is empty
      if (this.defineStore.cart.length === 0) return;
      const { isConfirmed } = await confirm({
        props: this,
        text: "clear_cart",
        text: "are_you_sure_clear_cart",
        con_txt: "confirm",
        can_txt: "cancel",
      });
      if (isConfirmed) {
        this.defineStore.deleteCart();
      }
    },
    async makeOrder() {
      if (this.defineStore.cart.length === 0) return;
      const { cart, txn } = await Help.cartArray(this.defineStore.cart);
      const body = {
        customer_id: this.customer_id,
        txn: txn,
        data: cart,
      };

      // Add table information if available
      if (this.selectedTable) {
        body.table_id = this.selectedTable.id;
      }

      const { data } = await this.$http.post("/make_cart_order", body);
      if (data && data.status) {
        // clear cart
        this.defineStore.deleteCart();
        this.SET_PAYMENT();
        await toast(this, data.message);
      }
    },

    async handleShowVariationModal({ id }) {
      const { data } = await this.$http.get(`/products/${id}`);
      this.selectedProduct = data.data;
      this.showVariationModal = true;
    },

    handleCloseVariationModal() {
      this.showVariationModal = false;
      this.selectedProduct = null;
    },

    async handleShowDynamicPriceModal({ id }) {
      const { data } = await this.$http.get(`/products/${id}`);
      this.selectedProductForDynamicPrice = data.data;
      this.showDynamicPriceModal = true;
    },

    handleCloseDynamicPriceModal() {
      this.showDynamicPriceModal = false;
      this.selectedProductForDynamicPrice = null;
    },

    handleShowPackModal(product) {
      this.selectedProductForPack = product;
      this.showPackModal = true;
    },

    handleClosePackModal() {
      this.showPackModal = false;
      this.selectedProductForPack = null;
    },

    loadCardsPerRow() {
      const savedCardsPerRow = localStorage.getItem("saleCardsPerRow");
      if (savedCardsPerRow) {
        this.cardsPerRow = parseInt(savedCardsPerRow, 10);
      } else {
        this.cardsPerRow = 4;
        localStorage.setItem("saleCardsPerRow", "4");
      }
    },
    handleStorageChange(event) {
      if (event.key === "saleCardsPerRow") {
        this.loadCardsPerRow();
      }
    },
    handleVisibilityChange() {
      // Reload cards per row when page becomes visible again
      if (!document.hidden) {
        this.loadCardsPerRow();
      }
    },
  },
};
</script>

<style>
.v-skeleton-loader__image.v-skeleton-loader__bone {
  height: 150px;
}
.v-skeleton-loader__card-heading .v-skeleton-loader__heading {
  margin: 8px 0px 0px 16px;
}
.v-skeleton-loader__list-item v-skeleton-loader__bone {
  height: 100%;
}
/* // sale-layout is the holder of the product cards and the footer, I need footer to be fixed at the bottom of the page height 50px and the product cards to be scrollable */

.sale-layout {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px); /* Full viewport minus app bar */
  min-height: 0;
  overflow: hidden;
  padding: 8px !important;
}

.sale-product-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.sale-layout--dark .sale-product-list {
  background: #171b21;
}

.sale-layout--dark {
  background: #171b21;
}

.sale-product-list::-webkit-scrollbar {
  width: 4px;
}

.sale-product-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
}

.sale-product-list::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

.sale-layout--dark .sale-product-list::-webkit-scrollbar-thumb,
.sale-layout--dark .sale-product-list::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}

/* Mobile adjustments - remove fixed positioning when footer is hidden */
@media (max-width: 767px) {
  .sale-layout {
    height: 100vh; /* Full height on mobile */
    padding-bottom: 0; /* No padding needed for hidden footer */
  }
}

/* Accessibility improvements */
.sale-layout button:focus {
  outline: 2px solid #5b39c9;
  outline-offset: 2px;
}

.sale-layout button:focus:not(:focus-visible) {
  outline: none;
}

/* Ensure buttons are always accessible */
/* Remove invalid CSS property for aria-hidden (handled in markup semantics) */

/* Prevent focus from being hidden */
.sale-layout *:focus {
  z-index: 1000;
}

/* Ensure row container properly wraps cards */
.sale-layout .row {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
}
</style>
